import h from 'hyperscript';

import type { StyleClass } from '@disy/lib/add-style-class';
import { partition } from '@disy/lib/partition';
import { groupBy } from '@disy/lib/type-util';
import { uniqueId } from '@disy/lib/unique-id';
import '@disy/ui/disclosure';
import type { MenuContent } from '@disy/ui/menu';

import type { AttributeListAttribute } from 'ui/attribute-list/attribute-list';
import { createResourceLabel } from 'ui/resource-label/resource-label';

import { AttributeCategory } from 'cadenza/repository/attribute-category';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { AttributeTileOptions } from 'cadenza/ui/attribute-tile/attribute-tile';
import { attributeTile } from 'cadenza/ui/attribute-tile/attribute-tile';
import type { AttributeTileAttribute } from 'cadenza/ui/attribute-tile/attribute-tile-attribute';
import { renderAttributesAndHierarchies } from 'cadenza/workbook/attribute-and-hierarchy-list-renderer/attribute-and-hierarchy-list-renderer';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';

import './create-attribute-list.css';

export type CreateAttributeListFunction = (
  attributeItems: HTMLElement[],
  labelId: string,
  options?: { styleClass?: StyleClass },
) => HTMLElement;

export interface EnrichmentAttributeGrouping {
  enrichmentId: ObjecttypeIdString;
  warningButtonFactory?: () => HTMLButtonElement | undefined;
  moreActionsButtonFactory?: () => HTMLButtonElement | undefined;
}

export interface CreateAttributeListGroupedByCategoryOptions {
  acceptedKeywords?: string[];
  hoverContext?: boolean;
  enrichmentAttributeGroupings?: EnrichmentAttributeGrouping[];
  openEnrichmentSections?: Set<string>;
  withManagementLink?: boolean;
  /** Function to create the individual attribute lists per attribute group. */
  createAttributeList: CreateAttributeListFunction;
  getAttributeActions?: FurtherActionsProvider;
  getAttributeById: (attributeId: AttributeId) => AttributeListAttribute;

  /**
   * When this is present, this group will not be hidden when the attributes
   * list is empty. Instead, an info alert will be displayed with the given text.
   */
  emptyGroupText?: string;

  /** Any options given here will override the standard options. These options will be applied to every tile in the list. */
  attributeTileOptions?: Pick<AttributeTileOptions, 'noDetails'>;
}

export type FurtherActionsProvider = (attribute: AttributeTileAttribute['id']) => ReturnType<MenuContent>;

/**
 * Creates list of attributes grouped by attribute type (measure/dimension).
 *
 * @param attributes - The entries to show in this list
 * @param options - Additional settings.
 * @return - the attribute elements.
 */
export function createAttributeListGroupedByCategory(
  attributes: AttributeListAttribute[],
  options: CreateAttributeListGroupedByCategoryOptions,
) {
  if (!attributes.length && options.emptyGroupText) {
    return [h('d-alert', { type: 'info', size: 's' }, options.emptyGroupText)];
  }
  return partition(attributes, ({ category }) => category === 'MEASURE')
    .reverse() // List dimensions (and hierarchies without heading) first
    .filter((attrs) => attrs.length > 0)
    .map((filteredAttributes, _, partitions) =>
      createAttributeItemList(filteredAttributes, options, partitions.length > 1),
    );
}

function createAttributeItemList(
  attributes: AttributeListAttribute[],
  options: CreateAttributeListGroupedByCategoryOptions,
  showCategoryLabels: boolean,
) {
  const { createAttributeList } = options;
  const labelId = uniqueId();
  let categoryLabel;
  if (showCategoryLabels) {
    const category = attributes[0].category; // All attributes are of the same category.
    categoryLabel = h('label', { id: labelId }, AttributeCategory.i18n(category, { count: 3 }));
  }

  let attributeList;
  const [attributesOfEnrichment, attributesOfNoEnrichment] = partition(attributes, (a) => a.enrichedObjecttypeId!);
  if (attributesOfNoEnrichment.length) {
    const attributeItems = renderAttributeItems(attributesOfNoEnrichment, options);
    attributeList = createAttributeList(attributeItems, labelId);
    attributeList.setAttribute('aria-labelledby', labelId);
  }

  const enrichments = [];
  if (attributesOfEnrichment.length) {
    const enrichmentAttributeGroupings = options.enrichmentAttributeGroupings ?? [];
    const attributesByEnrichment = groupBy(attributesOfEnrichment, (attribute) => attribute.objecttypeId!);
    for (const [enrichmentId, attributesOfEnrichmentObjecttype] of Object.entries(attributesByEnrichment)) {
      const items = renderAttributeItems(attributesOfEnrichmentObjecttype, options);
      const enrichedAttributeList = createAttributeList(items, labelId);
      const enrichmentObjecttypeId = enrichmentId as ObjecttypeIdString;
      const enrichmentAttributeGrouping = enrichmentAttributeGroupings.find(
        (eag) => eag.enrichmentId === enrichmentObjecttypeId,
      );
      if (enrichmentAttributeGrouping) {
        const enrichmentPrintName = attributesOfEnrichmentObjecttype[0].objecttypePrintName ?? '';
        const moreActionsButton = enrichmentAttributeGrouping.moreActionsButtonFactory?.();
        if (moreActionsButton) {
          moreActionsButton.slot = 'summary-aside';
        }
        const warningButton = enrichmentAttributeGrouping.warningButtonFactory?.();
        if (warningButton) {
          warningButton.slot = 'summary-aside';
        }
        const groupedEnrichment = h(
          'd-disclosure.d-workbook-section.d-enrichment-disclosure',
          {
            expanded: options.openEnrichmentSections?.has(enrichmentObjecttypeId),
            ontoggle: () =>
              groupedEnrichment.dispatchEvent(new CustomEvent('enrichment-details-toggle', { bubbles: true })),
            'data-attribute-group-id': enrichmentObjecttypeId,
          },
          [
            h('summary.ellipsis', createResourceLabel('enrichment', enrichmentPrintName)),
            moreActionsButton,
            warningButton,
            enrichedAttributeList,
          ],
        );
        enrichments.push(groupedEnrichment);
      } else {
        enrichedAttributeList.setAttribute('aria-labelledby', labelId);
        enrichments.push(enrichedAttributeList);
      }
    }
  }

  return h('.form-group', [categoryLabel, h('.d-stack-v.space-3', [attributeList, ...enrichments])]);
}

function renderAttributeItems(
  attributes: AttributeListAttribute[],
  options: CreateAttributeListGroupedByCategoryOptions,
) {
  const createItem = (attr: AttributeListAttribute) => createAttributeItem(attr, options);
  return renderAttributesAndHierarchies(attributes, createItem, createItem, options.getAttributeById);
}

function createAttributeItem(
  attribute: AttributeListAttribute,
  {
    hoverContext,
    withManagementLink,
    getAttributeActions,
    acceptedKeywords,
    attributeTileOptions,
  }: CreateAttributeListGroupedByCategoryOptions,
) {
  const attributeItem = attributeTile(attribute, {
    noDetails: true,
    noLinkToManagementCenter: !withManagementLink,
    hoverContext,
    menu: getAttributeActions && (() => getAttributeActions(attribute.id)),
    ...attributeTileOptions,
    disabled: !(attribute.selectable ?? true),
  });
  attributeItem.mark(acceptedKeywords);
  return attributeItem;
}
